import type { Meta, StoryObj } from '@storybook/angular';
import { moduleMetadata } from '@storybook/angular';
import { expect } from 'storybook/test';
import { StoryblokImageComponent } from '../../app/content/modules/storyblok/components/storyblok-image/storyblok-image.component';
import { StoryblokTextComponent } from '../../app/content/modules/storyblok/components/storyblok-text/storyblok-text.component';
import { StoryblokCountdownComponent } from '../../app/content/modules/storyblok/components/storyblok-countdown/storyblok-countdown.component';
import { StoryblokIconWithTextComponent } from '../../app/content/modules/storyblok/components/storyblok-icon-with-text/storyblok-icon-with-text.component';
import { withStoryblokDecorators } from '../_utils/story-helpers';

const meta: Meta = {
  title: 'Compositions/Storyblok Page Layout',
  tags: ['autodocs'],
  decorators: [
    ...withStoryblokDecorators(),
    moduleMetadata({
      imports: [
        StoryblokImageComponent,
        StoryblokTextComponent,
        StoryblokCountdownComponent,
        StoryblokIconWithTextComponent,
      ],
    }),
  ],
  parameters: {
    layout: 'fullscreen',
    docs: {
      description: {
        component: 'Demonstrates how Storyblok components compose into a typical CMS-driven page layout.',
      },
    },
  },
};
export default meta;
type Story = StoryObj;

export const PromotionalPage: Story = {
  render: () => ({
    template: `
      <div class="container py-4">
        <div class="row mb-4">
          <div class="col-12">
            <app-storyblok-image
              [asset]="heroImage"
              [general_styling]="[]">
            </app-storyblok-image>
          </div>
        </div>
        <div class="row mb-4">
          <div class="col-12 text-center">
            <app-countdown
              [countdown_datetime]="countdownDate"
              [title]="'Tilbuddet slutter om'"
              [general_styling]="[]">
            </app-countdown>
          </div>
        </div>
        <div class="row mb-4">
          <div class="col-12 col-md-8 mx-auto">
            <app-storyblok-text
              [body]="promoText"
              [general_styling]="[]">
            </app-storyblok-text>
          </div>
        </div>
        <div class="row g-3">
          <div class="col-12 col-md-4">
            <app-icon-with-text
              [icon]="{ filename: 'https://a.storyblok.com/f/106593/100x100/cc535ac665/bla-drue-tegning.jpg' }"
              [title]="'Gratis levering'"
              [text]="'Ved køb over 1.000 kr.'"
              [general_styling]="[]">
            </app-icon-with-text>
          </div>
          <div class="col-12 col-md-4">
            <app-icon-with-text
              [icon]="{ filename: 'https://a.storyblok.com/f/106593/100x100/f35c5f3002/gron-drue-tegning.jpg' }"
              [title]="'30 dages returret'"
              [text]="'Fuld pengene-tilbage-garanti'"
              [general_styling]="[]">
            </app-icon-with-text>
          </div>
          <div class="col-12 col-md-4">
            <app-icon-with-text
              [icon]="{ filename: 'https://a.storyblok.com/f/106593/100x100/cc535ac665/bla-drue-tegning.jpg' }"
              [title]="'Vingaranti'"
              [text]="'Ikke tilfreds? Penge retur'"
              [general_styling]="[]">
            </app-icon-with-text>
          </div>
        </div>
      </div>
    `,
    props: {
      heroImage: { id: 'sb-hero-1', filename: 'https://a.storyblok.com/f/106593/5459x1241/65f115b636/supervin_hvidorange_highres.png', alt: 'Supervin sommerkampagne' },
      countdownDate: '2026-12-31T23:59:59',
      promoText: { content: '<h2>Stor sommerkampagne</h2><p>Spar op til 40% på udvalgte vine fra hele verden. Vi har samlet de bedste tilbud fra vores vinlager.</p>' },
    },
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-storyblok-image')).toBeTruthy();
    await expect(canvasElement.querySelector('app-countdown')).toBeTruthy();
  },
};

export const TextAndImageGrid: Story = {
  render: () => ({
    template: `
      <div class="container py-4">
        <div class="row g-4">
          <div class="col-12 col-md-6">
            <app-storyblok-image
              [asset]="{ id: 'sb-grid-1', filename: 'https://a.storyblok.com/f/106593/100x100/cc535ac665/bla-drue-tegning.jpg', alt: 'Rødvin' }"
              [general_styling]="[]">
            </app-storyblok-image>
          </div>
          <div class="col-12 col-md-6 d-flex align-items-center">
            <app-storyblok-text
              [body]="{ content: '<h3>Vores rødvin</h3><p>Udforsk vores udvalg af premiumrødvine fra de bedste vinregioner i verden.</p>' }"
              [general_styling]="[]">
            </app-storyblok-text>
          </div>
        </div>
        <div class="row g-4 mt-2">
          <div class="col-12 col-md-6 d-flex align-items-center">
            <app-storyblok-text
              [body]="{ content: '<h3>Vores hvidvin</h3><p>Fra friske Chablis til aromatiske Riesling – vi har noget for enhver smag.</p>' }"
              [general_styling]="[]">
            </app-storyblok-text>
          </div>
          <div class="col-12 col-md-6">
            <app-storyblok-image
              [asset]="{ id: 'sb-grid-2', filename: 'https://a.storyblok.com/f/106593/100x100/f35c5f3002/gron-drue-tegning.jpg', alt: 'Hvidvin' }"
              [general_styling]="[]">
            </app-storyblok-image>
          </div>
        </div>
      </div>
    `,
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-storyblok-image')).toBeTruthy();
    await expect(canvasElement.querySelector('app-storyblok-text')).toBeTruthy();
  },
};
